import { ArrowUp, Check, Loader2, Mic, Square, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { transcribe } from "@/lib/api/ai";
import { cn } from "@/lib/utils/cn";
import { useVoiceRecorder } from "./use-voice-recorder";

/** Grows with its text up to this height, then scrolls. */
const MAX_HEIGHT = 240;

/** Touch keyboards have no Shift+Enter, so Enter adds a line there and the button sends. */
const enterSends = () => !window.matchMedia?.("(pointer: coarse)").matches;

const clock = (ms: number) => {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/**
 * The message box: a text field that grows as you type, voice messages (live bars, cancel or
 * confirm; the transcript lands in the text field to check before sending), send, and stop while
 * a reply streams.
 */
export function ChatComposer({
  value,
  onChange,
  onSend,
  onStop,
  streaming,
  loading = false,
  voice = true,
  language,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  streaming: boolean;
  /** The conversation is still loading: a message sent now would land outside it. */
  loading?: boolean;
  /** Whether the AI service can transcribe (the mic is hidden otherwise). */
  voice?: boolean;
  /** ISO 639-1 language of the voice message (the app language). */
  language?: string;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const field = useRef<HTMLTextAreaElement>(null);
  const [transcribing, setTranscribing] = useState(false);
  // The recording hits the time limit: it is used as if the user had confirmed it.
  const recorder = useVoiceRecorder({ onLimit: (audio) => void transcribeAudio(audio) });

  useLayoutEffect(() => {
    const element = field.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT)}px`;
  }, [value, recorder.state, transcribing]);

  async function transcribeAudio(audio: Blob | null) {
    if (!audio) return;
    setTranscribing(true);
    try {
      const text = await transcribe(audio, language);
      onChange(value.trim() ? `${value.trimEnd()} ${text}` : text);
      requestAnimationFrame(() => field.current?.focus());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("assistant.voice.failed"));
    } finally {
      setTranscribing(false);
    }
  }

  async function confirmRecording() {
    await transcribeAudio(await recorder.stop());
  }

  // Microphone refused, missing or broken: say why (each new failure once).
  const micError =
    recorder.error === "denied"
      ? t("assistant.voice.denied")
      : recorder.error === "unsupported"
        ? t("assistant.voice.unsupported")
        : recorder.error === "failed"
          ? t("assistant.voice.micFailed")
          : null;
  useEffect(() => {
    if (micError) toast.error(micError);
  }, [micError]);

  const recording = recorder.state !== "idle";
  const canSend = Boolean(value.trim()) && !streaming && !transcribing && !loading;

  return (
    <form
      className="rounded-2xl border bg-card shadow-sm transition-shadow focus-within:shadow-md"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSend) onSend();
      }}
      data-testid="chat-composer"
    >
      {recording ? (
        <div className="flex items-center gap-3 p-2 pl-4" data-testid="voice-recorder">
          <span className="size-2 shrink-0 animate-pulse rounded-full bg-destructive" aria-hidden />
          <div
            className="flex h-9 flex-1 items-center justify-end gap-[3px] overflow-hidden"
            role="img"
            aria-label={t("assistant.voice.recording")}
          >
            {recorder.levels.map((level, index) => (
              <span
                key={index}
                className="w-[3px] shrink-0 rounded-full bg-foreground/70 transition-[height] duration-75"
                style={{ height: `${Math.max(8, level * 100)}%` }}
              />
            ))}
          </div>
          <span className="w-10 text-right text-xs text-muted-foreground tabular-nums">
            {clock(recorder.elapsed)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={recorder.cancel}
            aria-label={t("assistant.voice.cancel")}
            data-testid="voice-cancel"
          >
            <X />
          </Button>
          <Button
            type="button"
            size="icon"
            className="rounded-full"
            onClick={() => void confirmRecording()}
            disabled={recorder.state !== "recording"}
            aria-label={t("assistant.voice.confirm")}
            data-testid="voice-confirm"
          >
            <Check />
          </Button>
        </div>
      ) : (
        <>
          <label htmlFor="chat-input" className="sr-only">
            {t("assistant.placeholder")}
          </label>
          <textarea
            id="chat-input"
            ref={field}
            rows={1}
            value={value}
            autoFocus={autoFocus}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && enterSends()) {
                event.preventDefault();
                if (canSend) onSend();
              }
            }}
            placeholder={
              transcribing ? t("assistant.voice.transcribing") : t("assistant.placeholder")
            }
            disabled={transcribing}
            className="block max-h-60 w-full resize-none bg-transparent px-4 pt-3.5 pb-1 text-base outline-none placeholder:text-muted-foreground md:text-sm"
            data-testid="chat-input"
          />
          <div className="flex items-center justify-end gap-1 p-2">
            {transcribing && (
              <Loader2 className="mr-auto ml-2 size-4 animate-spin text-muted-foreground" />
            )}
            {voice && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={() => void recorder.start()}
                disabled={streaming || transcribing}
                aria-label={t("assistant.voice.start")}
                data-testid="voice-start"
              >
                <Mic />
              </Button>
            )}
            {streaming ? (
              <Button
                type="button"
                size="icon"
                className="rounded-full"
                onClick={onStop}
                aria-label={t("assistant.stop")}
                data-testid="chat-stop"
              >
                <Square className="fill-current" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                className={cn("rounded-full", !canSend && "opacity-40")}
                disabled={!canSend}
                aria-label={t("assistant.send")}
                data-testid="chat-send"
              >
                <ArrowUp />
              </Button>
            )}
          </div>
        </>
      )}
    </form>
  );
}
